import React from 'react';
import { bool, func, node, number, object, oneOfType, shape, string } from 'prop-types';
import clsx from 'clsx';

import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import MuiSelect from '@material-ui/core/Select';
import useFormId from '../../hooks/useFormId';

import defaultClasses from './Select.module.css';

const Select = ({
    children,
    classes,
    disabled,
    error,
    errors,
    helperText,
    hidden,
    id,
    inputProps,
    isLoading,
    label,
    LabelProps,
    MenuProps,
    name,
    onBlur,
    onChange,
    required,
    SelectProps,
    touched,
    value
}) => {
    const baseId = useFormId(name || 'select');

    id || (id = baseId);

    if (hidden) {
        return null;
    }

    // Prevent prop warning in Material UI's Select component
    if (value === null || value === undefined) {
        value = '';
    }
    error || (error = touched && Boolean(errors));
    helperText =
        (isLoading && 'Loading...') ||
        (touched && errors) ||
        helperText;
    let inputLabel = null;
    if (label) {
        inputLabel = (
            <InputLabel
                classes={{
                    error: defaultClasses.labelError,
                    root:  defaultClasses.label
                }}
                htmlFor={id}
                {...LabelProps}
            >
                {label}
            </InputLabel>
        );
    }
    return (
        <FormControl
            classes={{
                root: clsx(defaultClasses.root, classes.root)
            }}
            error={Boolean(error)}
            required={required}
        >
            {inputLabel}
            <MuiSelect
                disabled={disabled || isLoading}
                inputProps={{
                    name,
                    onBlur,
                    id,
                    ...inputProps,
                    classes: {
                        root: clsx(
                            defaultClasses.input,
                            inputProps.classes.root
                        ),
                        ...inputProps.classes
                    }
                }}
                MenuProps={{
                    ...MenuProps,
                    MenuListProps: {
                        ...MenuProps.MenuListProps,
                        className: clsx(
                            defaultClasses.select,
                            MenuProps.MenuListProps.className
                        )
                    }
                }}
                onChange={onChange}
                value={value}
                {...SelectProps}
            >
                {children}
            </MuiSelect>
            <FormHelperText
                classes={{
                    root: defaultClasses.helperText
                }}
            >
                {helperText}
            </FormHelperText>
        </FormControl>
    );
};

Select.defaultProps = {
    classes:    {},
    disabled:   false,
    error:      false,
    helperText: null,
    hidden:     false,
    inputProps: {
        classes: {}
    },
    isLoading:  false,
    LabelProps: {},
    MenuProps:  {
        MenuListProps: {}
    },
    onBlur:      () => {},
    onChange:    () => {},
    required:    false,
    SelectProps: {},
    touched:     false
};

Select.propTypes = {
    children:   node,
    classes:    object,
    disabled:   bool,
    error:      bool,
    helperText: string,
    hidden:     bool,
    id:         string,
    inputProps: shape({
        classes: object,
        id:      string,
        name:    string
    }),
    isLoading:  bool,
    label:      string,
    LabelProps: object,
    MenuProps:  shape({
        MenuListProps: object
    }),
    name:        string,
    onBlur:      func,
    onChange:    func,
    required:    bool,
    SelectProps: object,
    touched:     bool,
    value:       oneOfType([number, object, string])
};

export default Select;
